import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import StatBox from "./StatBox";
import ClubBadge from "../ClubBadge";
import { getClubBadge } from "../../data/clubBadges";
import { card, panelHeading } from "../../styles";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { FPBand } from "../../lib/fpBands";
import { errorColor, calcPts } from "../../lib/fpScoring";
import { useIsMobile } from "../../hooks/useIsMobile";
import FPPositionsList from "./FPPositionsList";

interface Club {
  id: string;
  name: string;
  shortName: string | null;
}

interface Prediction {
  clubId: string;
  position: number;
}

interface ChartPoint {
  gameweek: string;
  gwScore: number;
  cumulative: number;
}

interface Props {
  clubs: Club[];
  predictions: Prediction[];
  actuals: Map<string, number>;
  N: number;
  bonus: number;
  memberScore: number;
  memberRank: number | null;
  memberCount: number;
  chartData: ChartPoint[];
  isOwnTeam: boolean;
  leagueStatus: "PREPARING" | "RUNNING" | "COMPLETED";
  /** Position-indexed bands (result[i] = band for position i + 1) — colors the predicted-position column. */
  bands: FPBand[];
}

const DIM = "var(--color-text-dim)";
const TEXT = "var(--color-text)";

export default function FPPredictionsView({
  clubs, predictions, actuals, N, bonus,
  memberScore, memberRank, memberCount, chartData, isOwnTeam,
  leagueStatus, bands,
}: Props) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isPreparing = leagueStatus === "PREPARING";
  const [hoveredClubId, setHoveredClubId] = useState<string | null>(null);
  const namedBands = bands.filter((b) => b.colorVar != null);
  const legend = namedBands.length > 0
    ? namedBands.filter((b, i) => namedBands.findIndex((o) => o.kind === b.kind) === i)
    : [];

  const sorted = [...predictions].sort((a, b) => a.position - b.position);
  const actualsSorted = [...predictions]
    .filter(p => actuals.has(p.clubId))
    .sort((a, b) => (actuals.get(a.clubId) ?? 999) - (actuals.get(b.clubId) ?? 999));

  const exactHits = sorted.filter(p => actuals.get(p.clubId) === p.position).length;
  const errors = sorted
    .filter(p => actuals.has(p.clubId))
    .map(p => Math.abs(p.position - (actuals.get(p.clubId) ?? 0)));
  const avgError = errors.length > 0 ? errors.reduce((a, b) => a + b, 0) / errors.length : null;

  const withError = sorted
    .filter(p => actuals.has(p.clubId))
    .map(p => {
      const actual = actuals.get(p.clubId)!;
      const error = Math.abs(p.position - actual);
      return { ...p, actual, error, pts: calcPts(error, N, bonus) };
    })
    .sort((a, b) => a.error - b.error || a.position - b.position);
  const bestCalls = withError.slice(0, 3);
  const worstCalls = [...withError].reverse().slice(0, 3);

  // SVG layout
  const ROW_HEIGHT = Math.min(28, Math.max(14, Math.floor(700 / Math.max(N, 1))));
  const FONT_SIZE = Math.max(9, Math.min(11, ROW_HEIGHT - 4));
  const CIRCLE_R = Math.max(2, ROW_HEIGHT / 10);
  const HEADER_H = 22;
  const VIZ_H = HEADER_H + N * ROW_HEIGHT;
  const TOTAL_W = 640;
  const LEFT_EDGE = 196;
  const RIGHT_EDGE = 444;

  const predYMap = new Map(sorted.map((p, i) => [p.clubId, HEADER_H + (i + 0.5) * ROW_HEIGHT]));
  const actualYMap = new Map(actualsSorted.map((p, i) => [p.clubId, HEADER_H + (i + 0.5) * ROW_HEIGHT]));

  const hasTrajectory = chartData.length > 1 && chartData.some(d => d.cumulative > 0);

  const panelHeader = (label: string) => (
    <div style={{ padding: "1.1rem 1.25rem 0.4rem" }}>
      <h2 style={{ ...panelHeading, margin: 0 }}>{label}</h2>
    </div>
  );

  const callRow = (c: typeof withError[0]) => {
    const club = clubs.find(cl => cl.id === c.clubId);
    const color = errorColor(c.error);
    return (
      <div key={c.clubId} style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.6rem 1rem", borderBottom: "1px solid var(--color-border-subtle)" }}>
        <ClubBadge config={getClubBadge(club?.name ?? "")} size={20} title={club?.name ?? ""} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "0.82rem", fontWeight: 600, color: TEXT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {club?.shortName ?? club?.name}
          </div>
          <div style={{ fontSize: "0.7rem", color: DIM, marginTop: 1 }}>
            {t("team.fp.predictedToActual", { predicted: c.position, actual: c.actual })}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontFamily: "var(--font-display)", fontSize: "1rem", fontWeight: 700, color, lineHeight: 1 }}>{c.pts}</div>
          <div style={{ fontSize: "0.62rem", color: DIM, marginTop: 1 }}>{t("team.fp.pts")}</div>
        </div>
      </div>
    );
  };

  const placeholderCallRow = (p: Prediction) => {
    const club = clubs.find(cl => cl.id === p.clubId);
    return (
      <div key={p.clubId} style={{ display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.6rem 1rem", borderBottom: "1px solid var(--color-border-subtle)", opacity: 0.35 }}>
        <ClubBadge config={getClubBadge(club?.name ?? "")} size={20} title={club?.name ?? ""} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "0.82rem", fontWeight: 600, color: TEXT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {club?.shortName ?? club?.name}
          </div>
          <div style={{ fontSize: "0.7rem", color: DIM, marginTop: 1 }}>{t("team.fp.predictedToActualPending", { predicted: p.position })}</div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontFamily: "var(--font-display)", fontSize: "1rem", fontWeight: 700, color: DIM, lineHeight: 1 }}>—</div>
          <div style={{ fontSize: "0.62rem", color: DIM, marginTop: 1 }}>{t("team.fp.pts")}</div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Stats strip — locked when preparing. 2-up on mobile (not StatBox's default 4-across,
          which ellipsises every label at this width); rank stays visible on mobile — it's
          mode-critical, unlike the other three which are detail. */}
      <div style={isMobile
        ? { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem", marginBottom: "1.25rem" }
        : { display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1.25rem" }}>
        <StatBox label={t("team.fp.score")} value={memberScore} sub={t("team.fp.total")} accent locked={isPreparing} borderColor="var(--color-accent)" />
        <StatBox label={t("team.fp.rank")} value={memberRank != null ? `#${memberRank}` : "—"} sub={t("team.fp.ofN", { n: memberCount })} locked={isPreparing} borderColor="var(--color-accent)" />
        <StatBox label={t("team.fp.exactHits")} value={exactHits} sub={t("team.fp.ofClubCount", { n: N })} locked={isPreparing} borderColor="var(--color-accent)" />
        <StatBox label={t("team.fp.avgError")} value={avgError != null ? avgError.toFixed(1) : "—"} sub={t("team.fp.positions")} locked={isPreparing} borderColor="var(--color-accent)" />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 260px", gap: "1.25rem", alignItems: "start" }}>
        {/* Predicted vs Actual: SVG parallel-coordinates chart on desktop, a scrollable per-club
            list on mobile — the chart's text scales with its container width (viewBox-based),
            and the fixed 260px right rail used to leave it unreadably small below ~400px. */}
        <div style={{ ...card, padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "1.1rem 1.25rem 0.4rem" }}>
            <h2 style={{ ...panelHeading, margin: 0 }}>
              {t("team.fp.predictedVsActual")}
            </h2>
          </div>
          {legend.length > 0 && (
            <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", padding: "0 1.25rem 0.6rem" }}>
              {legend.map((band) => (
                <div key={band.kind} style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                  <span style={{ width: 6, height: 6, borderRadius: "50%", background: `var(${band.colorVar})`, flexShrink: 0 }} />
                  <span style={{ fontSize: "0.7rem", fontWeight: 600, color: `var(${band.colorVar})` }}>{band.label}</span>
                </div>
              ))}
            </div>
          )}
          {isMobile ? (
            <FPPositionsList clubs={clubs} sorted={sorted} actuals={actuals} N={N} bonus={bonus} isPreparing={isPreparing} bands={bands} />
          ) : (
          <div style={{ padding: "1rem 0.5rem", overflowY: "auto", maxHeight: "760px" }}>
            <svg viewBox={`0 0 ${TOTAL_W} ${VIZ_H}`} style={{ width: "100%", display: "block" }}>
              {/* Band backgrounds + divider lines — top tier tinted green, relegation tinted red.
                  A band's divider sits on whichever edge actually separates it from the rest of
                  the table (never at row 0 or row N, where there's nothing to separate it from). */}
              {legend.map((band) => {
                const y0 = HEADER_H + (band.startPosition - 1) * ROW_HEIGHT;
                const y1 = HEADER_H + band.endPosition * ROW_HEIGHT;
                return (
                  <g key={`band-${band.kind}`}>
                    <rect x={0} y={y0} width={TOTAL_W} height={y1 - y0} fill={`var(${band.colorVar}-bg)`} />
                    {band.startPosition > 1 && (
                      <line x1={0} y1={y0} x2={TOTAL_W} y2={y0} stroke={`var(${band.colorVar})`} strokeWidth={1} strokeOpacity={0.5} />
                    )}
                    {band.endPosition < N && (
                      <line x1={0} y1={y1} x2={TOTAL_W} y2={y1} stroke={`var(${band.colorVar})`} strokeWidth={1} strokeOpacity={0.5} />
                    )}
                  </g>
                );
              })}

              {/* Column headers */}
              <text x={LEFT_EDGE - 12} y={16} textAnchor="end" fontSize={8} fontWeight={700} letterSpacing={1} fill={DIM}>{t("team.fp.predicted")}</text>
              <text x={RIGHT_EDGE + 12} y={16} textAnchor="start" fontSize={8} fontWeight={700} letterSpacing={1} fill={DIM}>{t("team.fp.actual")}</text>

              {/* Bezier curves — only when not preparing */}
              {!isPreparing && sorted.map(p => {
                const py = predYMap.get(p.clubId);
                const ay = actualYMap.get(p.clubId);
                if (py === undefined || ay === undefined) return null;
                const error = actuals.has(p.clubId) ? Math.abs(p.position - actuals.get(p.clubId)!) : 0;
                const color = errorColor(error);
                const isHov = hoveredClubId === p.clubId;
                const isDim = hoveredClubId !== null && !isHov;
                const opacity = isDim ? 0.07 : isHov ? 1 : 0.55;
                const cx1 = LEFT_EDGE + (RIGHT_EDGE - LEFT_EDGE) * 0.4;
                const cx2 = LEFT_EDGE + (RIGHT_EDGE - LEFT_EDGE) * 0.6;
                return (
                  <g key={`curve-${p.clubId}`} style={{ cursor: "pointer" }}
                    onMouseEnter={() => setHoveredClubId(p.clubId)}
                    onMouseLeave={() => setHoveredClubId(null)}>
                    <path d={`M ${LEFT_EDGE} ${py} C ${cx1} ${py}, ${cx2} ${ay}, ${RIGHT_EDGE} ${ay}`}
                      fill="none" stroke={color} strokeWidth={isHov ? 2 : 1.5} opacity={opacity} />
                    <circle cx={LEFT_EDGE} cy={py} r={isHov ? CIRCLE_R + 1 : CIRCLE_R} fill={color} opacity={opacity} />
                    <circle cx={RIGHT_EDGE} cy={ay} r={isHov ? CIRCLE_R + 1 : CIRCLE_R} fill={color} opacity={opacity} />
                  </g>
                );
              })}

              {/* Left labels: [club name] [pos#] ● */}
              {sorted.map((p, i) => {
                const y = HEADER_H + (i + 0.5) * ROW_HEIGHT;
                const club = clubs.find(c => c.id === p.clubId);
                const label = club?.shortName ?? club?.name ?? p.clubId;
                const isHov = !isPreparing && hoveredClubId === p.clubId;
                const isDim = !isPreparing && hoveredClubId !== null && !isHov;
                const band = bands[p.position - 1];
                const posColor = isHov ? "var(--color-accent)" : (band?.colorVar ? `var(${band.colorVar})` : DIM);
                return (
                  <g key={`left-${p.clubId}`} style={{ cursor: isPreparing ? "default" : "pointer" }}
                    onMouseEnter={() => !isPreparing && setHoveredClubId(p.clubId)}
                    onMouseLeave={() => !isPreparing && setHoveredClubId(null)}
                    opacity={isDim ? 0.2 : 1}>
                    <text x={LEFT_EDGE - CIRCLE_R - 5} y={y + FONT_SIZE * 0.35} textAnchor="end"
                      fontSize={FONT_SIZE} fontWeight={700}
                      fill={posColor}>{p.position}</text>
                    <text x={LEFT_EDGE - CIRCLE_R - 5 - FONT_SIZE - 4} y={y + FONT_SIZE * 0.35} textAnchor="end"
                      fontSize={FONT_SIZE} fontWeight={isHov ? 600 : 400}
                      fill={TEXT}>{label}</text>
                  </g>
                );
              })}

              {/* Right labels: actual positions or dim placeholders */}
              {isPreparing
                ? sorted.map((p, i) => {
                    const y = HEADER_H + (i + 0.5) * ROW_HEIGHT;
                    const club = clubs.find(c => c.id === p.clubId);
                    const label = club?.shortName ?? club?.name ?? p.clubId;
                    return (
                      <g key={`right-${p.clubId}`} opacity={0.25}>
                        <text x={RIGHT_EDGE + CIRCLE_R + 5} y={y + FONT_SIZE * 0.35} textAnchor="start"
                          fontSize={FONT_SIZE} fontWeight={700} fill={DIM}>—</text>
                        <text x={RIGHT_EDGE + CIRCLE_R + 5 + FONT_SIZE + 4} y={y + FONT_SIZE * 0.35} textAnchor="start"
                          fontSize={FONT_SIZE} fontWeight={400} fill={TEXT}>{label}</text>
                      </g>
                    );
                  })
                : actualsSorted.map((p, i) => {
                    const y = HEADER_H + (i + 0.5) * ROW_HEIGHT;
                    const club = clubs.find(c => c.id === p.clubId);
                    const label = club?.shortName ?? club?.name ?? p.clubId;
                    const actualPos = actuals.get(p.clubId);
                    const error = Math.abs(p.position - (actualPos ?? 0));
                    const pts = calcPts(error, N, bonus);
                    const isHov = hoveredClubId === p.clubId;
                    const isDim = hoveredClubId !== null && !isHov;
                    return (
                      <g key={`right-${p.clubId}`} style={{ cursor: "pointer" }}
                        onMouseEnter={() => setHoveredClubId(p.clubId)}
                        onMouseLeave={() => setHoveredClubId(null)}
                        opacity={isDim ? 0.2 : 1}>
                        <text x={RIGHT_EDGE + CIRCLE_R + 5} y={y + FONT_SIZE * 0.35} textAnchor="start"
                          fontSize={FONT_SIZE} fontWeight={700}
                          fill={isHov ? "var(--color-accent)" : DIM}>{actualPos}</text>
                        <text x={RIGHT_EDGE + CIRCLE_R + 5 + FONT_SIZE + 4} y={y + FONT_SIZE * 0.35} textAnchor="start"
                          fontSize={FONT_SIZE} fontWeight={isHov ? 600 : 400}
                          fill={TEXT}>{label}</text>
                        <text x={TOTAL_W - 4} y={y + FONT_SIZE * 0.35} textAnchor="end"
                          fontSize={FONT_SIZE} fontWeight={700}
                          fill={errorColor(error)}>{pts}</text>
                      </g>
                    );
                  })
              }
            </svg>
          </div>
          )}
        </div>

        {/* Right column */}
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          {/* Best Calls */}
          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            {panelHeader(t("team.fp.bestCalls"))}
            {isPreparing
              ? sorted.slice(0, 3).map(p => placeholderCallRow(p))
              : bestCalls.map(c => callRow(c))}
          </div>

          {/* Worst Calls */}
          <div style={{ ...card, padding: 0, overflow: "hidden" }}>
            {panelHeader(t("team.fp.worstCalls"))}
            {isPreparing
              ? sorted.slice(-3).reverse().map(p => placeholderCallRow(p))
              : worstCalls.map(c => callRow(c))}
          </div>

          {/* Trajectory */}
          {hasTrajectory && (
            <div style={{ ...card, padding: 0, overflow: "hidden" }}>
              {panelHeader(isOwnTeam ? t("team.fp.yourTrajectory") : t("team.fp.trajectory"))}
              <div style={{ padding: "0.75rem" }}>
                <ResponsiveContainer width="100%" height={120}>
                  <AreaChart data={[{ gameweek: "", cumulative: 0 }, ...chartData]} margin={{ top: 4, right: 4, bottom: 0, left: -20 }}>
                    <defs>
                      <linearGradient id="fp-traj" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--color-accent)" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="var(--color-accent)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border-subtle)" />
                    <XAxis dataKey="gameweek" tick={{ fontSize: 8, fill: DIM }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 8, fill: DIM }} axisLine={false} tickLine={false} domain={[0, "auto"]} />
                    <Tooltip contentStyle={{ background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: 8, fontSize: "0.75rem" }} />
                    <Area type="monotone" dataKey="cumulative" stroke="var(--color-accent)" strokeWidth={2} fill="url(#fp-traj)" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
